// src/components/admin/TransportList.js
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, generatePath } from 'react-router-dom';
import AddBoxIcon from '@mui/icons-material/AddBoxOutlined';
import LocalShippingIcon from '@mui/icons-material/LocalShippingOutlined';
import { AdminShell } from './Sidebar';
import { TransportProfilePageRoute, CreateTransportPageRoute } from '../../consts';
import {
  StatCard, RefreshButton, FilterPill, SearchField,
  TableCard, LoadingState, EmptyState, Pagination,
} from '../ui/CrmPage';

function desc(a, b, getVal) {
  const av = getVal(a); const bv = getVal(b);
  if (bv == null && av != null) return -1;
  if (av == null && bv != null) return 1;
  if (bv < av) return -1;
  if (bv > av) return 1;
  return 0;
}
const getComparator = (order, getter) =>
  order === 'desc' ? (a, b) => desc(a, b, getter) : (a, b) => -desc(a, b, getter);
const stableSort = (arr, comp) =>
  arr.map((el, i) => [el, i]).sort((a, b) => { const o = comp(a[0], b[0]); return o || a[1] - b[1]; }).map((el) => el[0]);

const sortIcon = (active, dir) => (
  <span className={`crmpage__sort ${active ? 'is-active' : ''}`} aria-hidden>
    {active ? (dir === 'asc' ? '▲' : '▼') : '↕'}
  </span>
);

export default function TransportList() {
  const navigate = useNavigate();
  const apiBase = useMemo(() => {
    const base = process.env.REACT_APP_API_URL || '';
    return base.endsWith('/') ? base : `${base}/`;
  }, []);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);

  const [orgMap, setOrgMap] = useState({});
  const [divMap, setDivMap] = useState({});

  const organizationName = (r) =>
    r.organization_name ?? orgMap[String(r.id_organization)] ?? (r.id_organization != null ? String(r.id_organization) : '');
  const divisionName = (r) =>
    r.division_name ?? divMap[String(r.id_division)] ?? (r.id_division != null ? String(r.id_division) : '');

  useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      setLoading(true); setError('');
      try {
        const [vRes, orgRes, divRes] = await Promise.all([
          fetch(`${apiBase}v1/vehicles?sortBy=id&orderBy=asc`, { signal: ctrl.signal }),
          fetch(`${apiBase}v1/organizations`, { signal: ctrl.signal }).catch(() => null),
          fetch(`${apiBase}v1/divisions`, { signal: ctrl.signal }).catch(() => null),
        ]);
        if (!vRes.ok) {
          const t = await vRes.text().catch(() => '');
          throw new Error(`HTTP ${vRes.status} ${vRes.statusText} ${t}`.trim());
        }
        const v = await vRes.json();
        setRows(Array.isArray(v) ? v : []);

        const oMap = {}, dMap = {};
        if (orgRes?.ok) {
          const orgs = await orgRes.json();
          (Array.isArray(orgs) ? orgs : []).forEach((o) => {
            const id = String(o.id ?? o.id_organization ?? '');
            if (id) oMap[id] = o.name ?? o.organization_name ?? id;
          });
        }
        if (divRes?.ok) {
          const divs = await divRes.json();
          (Array.isArray(divs) ? divs : []).forEach((d) => {
            const id = String(d.id ?? d.id_division ?? '');
            if (id) dMap[id] = d.name ?? d.division_name ?? id;
          });
        }
        setOrgMap(oMap);
        setDivMap(dMap);
      } catch (e) {
        if (e.name !== 'AbortError') setError(e.message || 'Ошибка загрузки');
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, [apiBase, reloadKey]);

  const [q, setQ] = useState('');
  const [orgFilter, setOrgFilter] = useState('all');
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('id');
  const [page, setPage] = useState(1);
  const rowsPerPage = 15;

  const totals = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => !r.is_deleted).length;
    const orgs = new Set(rows.map((r) => organizationName(r)).filter(Boolean)).size;
    const divs = new Set(rows.map((r) => divisionName(r)).filter(Boolean)).size;
    return { total, active, orgs, divs };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, orgMap, divMap]);

  const orgOptions = useMemo(() => {
    const names = new Set();
    rows.forEach((r) => {
      const name = (organizationName(r) || '').trim();
      if (name) names.add(name);
    });
    return Array.from(names);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, orgMap]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return rows.filter((r) => {
      const orgName = (organizationName(r) || '').trim();
      if (orgFilter !== 'all' && orgName !== orgFilter) return false;
      if (!term) return true;
      return (
        (r.name || '').toLowerCase().includes(term) ||
        (r.registration_number || '').toLowerCase().includes(term) ||
        (r.document || '').toLowerCase().includes(term) ||
        orgName.toLowerCase().includes(term) ||
        (divisionName(r) || '').toLowerCase().includes(term)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, q, orgFilter, orgMap, divMap]);

  const sortGetter = useMemo(() => {
    switch (orderBy) {
      case 'id':                  return (r) => Number(r.id || 0);
      case 'name':                return (r) => (r.name || '').toLowerCase();
      case 'registration_number': return (r) => (r.registration_number || '').toLowerCase();
      case 'id_organization':     return (r) => (organizationName(r) || '').toLowerCase();
      case 'id_division':         return (r) => (divisionName(r) || '').toLowerCase();
      default:                    return (r) => r[orderBy] ?? '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderBy, orgMap, divMap]);

  const sorted = useMemo(
    () => stableSort(filtered, getComparator(order, sortGetter)),
    [filtered, order, sortGetter]
  );
  const totalPages = Math.max(1, Math.ceil(sorted.length / rowsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return sorted.slice(start, start + rowsPerPage);
  }, [sorted, page]);
  useEffect(() => { if (page > totalPages) setPage(1); }, [totalPages, page]);

  const requestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const hasFilters = q || orgFilter !== 'all';
  const resetFilters = () => { setQ(''); setOrgFilter('all'); setPage(1); };

  return (
    <AdminShell
      breadcrumbs="Транспорт"
      title="Транспорт"
      subtitle="Транспортные средства, номера, подразделения"
      actions={
        <button
          type="button"
          className="adminbtn adminbtn--primary"
          onClick={() => navigate(CreateTransportPageRoute)}
        >
          <AddBoxIcon sx={{ fontSize: 18 }} />
          Добавить
        </button>
      }
    >
      <div className="crmpage crmpage--embedded">
        <div className="crmpage__main">
          <section className="crmpage__stats">
            <StatCard label="Всего ТС"      value={totals.total}  accent="neutral" />
            <StatCard label="Активных"      value={totals.active} accent="success" />
            <StatCard label="Организаций"   value={totals.orgs}   accent="brand" />
            <StatCard label="Подразделений" value={totals.divs}   accent="info" />
            <RefreshButton onClick={() => setReloadKey((k) => k + 1)} />
          </section>

          <section className="crmpage__filters">
            <SearchField
              value={q}
              onChange={(e) => { setQ(e.target.value); setPage(1); }}
              placeholder="Поиск: название, гос. номер, документ…"
            />
            <div className="crmpage__pills">
              <FilterPill active={orgFilter === 'all'} onClick={() => { setOrgFilter('all'); setPage(1); }}>Все организации</FilterPill>
              {orgOptions.map((name) => (
                <FilterPill
                  key={name}
                  active={orgFilter === name}
                  onClick={() => { setOrgFilter(name); setPage(1); }}
                >
                  {name}
                </FilterPill>
              ))}
            </div>
            {hasFilters && (
              <button type="button" className="crmpage__reset" onClick={resetFilters}>Сбросить</button>
            )}
          </section>

          {error && <div className="crmpage__alert crmpage__alert--error">Не удалось загрузить транспорт. {error}</div>}

          <TableCard>
            {loading ? (
              <LoadingState text="Загружаем транспорт…" />
            ) : paged.length === 0 ? (
              <EmptyState
                title="Транспорт не найден"
                text={hasFilters ? 'Сбросьте фильтры или измените запрос.' : 'Добавьте первое ТС.'}
              />
            ) : (
              <>
                <div className="crmpage__table-wrap">
                  <table className="crmpage__table">
                    <colgroup>
                      <col style={{ width: 70 }} />
                      <col />
                      <col style={{ width: 160 }} />
                      <col style={{ width: 220 }} />
                      <col style={{ width: 220 }} />
                      <col style={{ width: 110 }} />
                    </colgroup>
                    <thead>
                      <tr>
                        <th onClick={() => requestSort('id')}                  style={{ cursor: 'pointer' }}>ID {sortIcon(orderBy === 'id', order)}</th>
                        <th onClick={() => requestSort('name')}                style={{ cursor: 'pointer' }}>Название {sortIcon(orderBy === 'name', order)}</th>
                        <th onClick={() => requestSort('registration_number')} style={{ cursor: 'pointer' }}>Гос. номер {sortIcon(orderBy === 'registration_number', order)}</th>
                        <th onClick={() => requestSort('id_organization')}     style={{ cursor: 'pointer' }}>Организация {sortIcon(orderBy === 'id_organization', order)}</th>
                        <th onClick={() => requestSort('id_division')}         style={{ cursor: 'pointer' }}>Подразделение {sortIcon(orderBy === 'id_division', order)}</th>
                        <th>Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((r) => (
                        <tr
                          key={r.id}
                          className="crmpage__row"
                          onClick={() => navigate(generatePath(TransportProfilePageRoute, { id: r.id }))}
                        >
                          <td className="crmpage__date">{r.id}</td>
                          <td>
                            <div className="crmpage__user-cell">
                              <span className="crmpage__avatar" aria-hidden>
                                <LocalShippingIcon sx={{ fontSize: 16, color: '#fff' }} />
                              </span>
                              <div style={{ minWidth: 0 }}>
                                <div className="crmpage__primary">{r.name || '—'}</div>
                                {r.document && (
                                  <div className="crmpage__sub">Документ: {r.document}</div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="crmpage__num">{r.registration_number || '—'}</td>
                          <td className="crmpage__muted">{organizationName(r) || '—'}</td>
                          <td className="crmpage__muted">{divisionName(r) || '—'}</td>
                          <td>
                            <span className={`crmpage__status crmpage__status--${r.is_deleted ? 'neutral' : 'success'}`}>
                              {r.is_deleted ? 'Удалено' : 'Активно'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <Pagination current={page} total={totalPages} onChange={setPage} />
              </>
            )}
          </TableCard>
        </div>
      </div>
    </AdminShell>
  );
}
